import {LibrarySongComplete} from './graphql/types/LibrarySongComplete';
import {CatalogSongComplete} from './graphql/types/CatalogSongComplete';
import {ArtworkComplete} from './graphql/types/ArtworkComplete';
import {LibraryAlbumComplete} from './graphql/types/LibraryAlbumComplete';
import {CatalogAlbumComplete} from './graphql/types/CatalogAlbumComplete';
import {LibraryAlbumWithSongsFragment} from './graphql/types/LibraryAlbumWithSongsFragment';
import {CatalogAlbumWithSongsFragment} from './graphql/types/CatalogAlbumWithSongsFragment';
import {LibraryPlaylistComplete} from './graphql/types/LibraryPlaylistComplete';

export enum Page {
  Home = 'home',
  Library = 'library',
  Browse = 'browse',
  Feed = 'feed',
  Settings = 'settings',
  Onboarding = 'onboarding',
  Login = 'login',
  Search = 'search',
  UpdateAccount = 'update_account',
  Logout = 'logout',
  CreateAccount = 'create_account',
}

export interface PageDetails {
  id: Page;
  title: string;
}

export enum UserStatus {
  Hosting = 'hosting', // Blue
  Connected = 'connected', // Blue
  Listening = 'listening', // Green
  Idle = 'idle', // Green
  Offline = 'offline', // Grey
}

export enum Platform {
  Spotify = 'spotify',
  Soundcloud = 'soundcloud',
  AppleMusic = 'apple music',
}

export enum AlbumType {
  Album = 'album',
  Compilation = 'compilation',
  Single = 'single',
}

export type Artwork = Pick<
  ArtworkComplete,
  | 'smallUrl'
  | 'mediumUrl'
  | 'largeUrl'
  | 'textColor1'
  | 'textColor2'
  | 'textColor3'
  | 'textColor4'
  | 'backgroundColor'
>;

export type LibrarySong = SafeOverride<
  Pick<
    LibrarySongComplete,
    | 'id'
    | 'name'
    | 'trackNumber'
    | 'explicit'
    | 'albumName'
    | 'artistName'
    | 'duration'
    | 'artwork'
  >,
  {artwork: Artwork}
>;

export type CatalogSong = SafeOverride<
  Pick<
    CatalogSongComplete,
    | 'id'
    | 'name'
    | 'trackNumber'
    | 'explicit'
    | 'albumName'
    | 'artistName'
    | 'duration'
    | 'releaseDate'
    | 'artwork'
    | 'likes'
    | 'liked'
  >,
  {artwork: Artwork}
>;

type SafeOverride<T1, T2> = (Omit<T1, keyof T2> & T2) | T1;

export type LibraryAlbum = SafeOverride<
  Pick<
    LibraryAlbumComplete,
    'id' | 'name' | 'trackCount' | 'artistName' | 'dateAdded' | 'artwork'
  >,
  {artwork: Artwork}
>;

export type LibraryPlaylist = Pick<
  LibraryPlaylistComplete,
  'id' | 'name' | 'dateAdded' | 'curatorName' | 'artwork'
>;

export type CatalogAlbum = SafeOverride<
  Pick<
    CatalogAlbumComplete,
    | 'id'
    | 'name'
    | 'trackCount'
    | 'artistName'
    | 'explicit'
    | 'releaseDate'
    | 'artwork'
    | 'isCompilation'
    | 'isSingle'
  >,
  {artwork: Artwork}
>;

export type LibraryAlbumWithSongs =
  | LibraryAlbumWithSongsFragment
  | (LibraryAlbum & {tracks: LibrarySong[]});

export type CatalogAlbumWithSongs =
  | CatalogAlbumWithSongsFragment
  | (CatalogAlbum & {tracks: CatalogSong[]});

export type Song = LibrarySong | CatalogSong;

export type Album = LibraryAlbum | CatalogAlbum;

export type AlbumWithSongs = LibraryAlbumWithSongs | CatalogAlbumWithSongs;

export interface UserListeningDetails {
  song: string;
  artist: string;
}

export interface User {
  id: string;
  name: string;
  profilePhoto: string;
  online?: boolean;
  status?: UserStatus;
  listeningDetails?: UserListeningDetails;
}
